// eslint-disable
import React, { MutableRefObject, useCallback, useRef } from 'react';
import { MapRef } from 'react-map-gl';
import type { LegendOrientation } from '../MapView/legend';

export type MapCaptureApi = {
  captureScreenshot: () => Promise<void>;
};

export type MapLegendApi = {
  exportLegend: (orientation: LegendOrientation) => Promise<void>;
};

type GoToValue = {
  mapRef: MutableRefObject<MapRef>;
  goTo: (longitude: number, latitude: number, zoom?: number) => void;
  fitToBounds: (bounds: [number, number, number, number]) => void;
  registerMapCapture: (api: MapCaptureApi | null) => void;
  captureMapScreenshot: () => Promise<void>;
  registerMapLegend: (api: MapLegendApi | null) => void;
  exportLegend: (orientation: LegendOrientation) => Promise<void>;
};

function createCtx(defaultValue: GoToValue) {
  const ctx = React.createContext(defaultValue);

  function GoToProvider(props: React.PropsWithChildren<{}>) {
    const mapRef = useRef(null);
    const captureApiRef = useRef<MapCaptureApi | null>(null);
    const legendApiRef = useRef<MapLegendApi | null>(null);

    const goTo = useCallback(
      (longitude: number, latitude: number, zoom?: number) => {
        if (!mapRef.current) {
          return;
        }

        mapRef.current.flyTo({
          center: [longitude, latitude],
          ...(zoom == null ? {} : { zoom }),
          duration: zoom == null ? 0 : 500,
        });
      },
      []
    );

    const fitToBounds = useCallback(
      (bounds: [number, number, number, number]) => {
        if (!mapRef.current) {
          return;
        }

        mapRef.current.fitBounds(
          [
            [bounds[0], bounds[1]],
            [bounds[2], bounds[3]],
          ],
          { padding: 48, duration: 500, maxZoom: 16 }
        );
      },
      []
    );

    const registerMapCapture = useCallback((api: MapCaptureApi | null) => {
      captureApiRef.current = api;
    }, []);

    const captureMapScreenshot = useCallback(async () => {
      if (!captureApiRef.current) return;
      await captureApiRef.current.captureScreenshot();
    }, []);

    const registerMapLegend = useCallback((api: MapLegendApi | null) => {
      legendApiRef.current = api;
    }, []);

    const exportLegend = useCallback(async (orientation: LegendOrientation) => {
      if (!legendApiRef.current) return;
      await legendApiRef.current.exportLegend(orientation);
    }, []);

    const value = React.useMemo(
      () => ({
        mapRef,
        goTo,
        fitToBounds,
        registerMapCapture,
        captureMapScreenshot,
        registerMapLegend,
        exportLegend,
      }),
      [
        goTo,
        fitToBounds,
        registerMapCapture,
        captureMapScreenshot,
        registerMapLegend,
        exportLegend,
      ]
    );

    return <ctx.Provider value={value} {...props} />;
  }

  return [ctx, GoToProvider] as [typeof ctx, typeof GoToProvider];
}

const [GoToContext, GoToProvider] = createCtx(null);

export { GoToContext, GoToProvider };
